// S'abonner à Radar (issues #109, #112, #121). La page où mènent le bandeau de
// fin d'accès, le site vitrine et le retour de Stripe : l'essai en cours, puis
// les deux formules (Radar, Complet), au mois ou à l'année.
//
// LE RETOUR DE STRIPE NE PROUVE RIEN. `?caisse=ok` dit seulement que le
// navigateur est revenu : l'abonnement n'existe qu'une fois le webhook vérifié
// arrivé. La page relit donc l'état toutes les trois secondes et dit « en cours
// de confirmation » tant qu'il n'est pas là, au plus deux minutes, puis
// renvoie au mail de Stripe plutôt que d'attendre pour toujours.
//
// Les prix viennent du serveur, qui les lit dans le catalogue Stripe : aucun
// montant n'est écrit ici. Les deux lignes de chaque formule, si : elles
// disent ce que `QUOTAS` (src/services/accesRadar.ts) applique.

import { useEffect, useState } from 'react'
import { CreditCard } from 'lucide-react'
import { get, post } from '../lib/api'
import { useData, Loading, ActionButton, Empty } from '../components/common'
import { useT } from '../lib/i18n'
import { useLang } from '../lib/lang'
import { LOCALE_UI, langueUi } from '@ai-job-fit/engine/langues'
import { fullDate } from '../lib/format'
import { parseQuery } from '../lib/router'

type Plan = 'radar' | 'complet'
type Intervalle = 'mois' | 'an'
interface PrixAffiche {
  montant: number
  devise: string
  intervalle: string
}

interface EtatAbonnement {
  abonne: boolean
  statut: string | null
  plan: Plan | null
  jusquA: string | null
  offert: boolean
  /** Ce qui ouvre l'accès : offert, abonnement, essai, ou rien (#112). */
  raison: 'offert' | 'abonnement' | 'essai' | 'aucune'
  essaiJusquA: string | null
  catalogue: Partial<Record<Plan, Partial<Record<Intervalle, PrixAffiche>>>> | null
  ouvert: boolean
  /** Un client Stripe connu : le portail gère carte, factures et résiliation (#113). */
  gerable: boolean
}

const ATTENTE_MAX_MS = 2 * 60 * 1000

export function prixLisible(p: { montant: number; devise: string }, lang: string): string {
  return new Intl.NumberFormat(LOCALE_UI[langueUi(lang)], { style: 'currency', currency: p.devise.toUpperCase() }).format(p.montant / 100)
}

export function Abonnement() {
  const t = useT()
  const lang = useLang()
  const retour = parseQuery(location.hash).get('caisse')
  const etat = useData(() => get<EtatAbonnement>('/api/abonnement'), [])
  const [attente, setAttente] = useState(retour === 'ok')
  const [intervalle, setIntervalle] = useState<Intervalle>('mois')

  // Attendre le webhook : relire tant que l'abonnement n'est pas là.
  useEffect(() => {
    if (!attente) return
    if (etat.data?.abonne) {
      setAttente(false)
      return
    }
    const debut = Date.now()
    const minuterie = setInterval(() => {
      if (Date.now() - debut > ATTENTE_MAX_MS) {
        setAttente(false)
        clearInterval(minuterie)
      } else etat.reload()
    }, 3000)
    return () => clearInterval(minuterie)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attente, etat.data?.abonne])

  if (etat.loading && !etat.data) return <Loading />
  const e = etat.data
  if (!e) return <Empty>{etat.error}</Empty>

  const parIntervalle = (p: PrixAffiche) => (p.intervalle === 'year' ? t('abonnement.parAn') : t('abonnement.parMois'))
  const nomPlan = (p: Plan) => (p === 'complet' ? t('abonnement.complet') : t('abonnement.radar'))
  // Une phrase sur l'état du compte, en tête.
  const situation =
    e.raison === 'offert'
      ? t('abonnement.offert')
      : e.raison === 'essai' && e.essaiJusquA
        ? t('abonnement.essaiJusqua', { date: fullDate(e.essaiJusquA) })
        : e.raison === 'aucune' && !e.abonne && e.essaiJusquA
          ? t('abonnement.essaiFini', { date: fullDate(e.essaiJusquA) })
          : null
  const anneeProposee = Boolean(e.catalogue?.radar?.an || e.catalogue?.complet?.an)

  return (
    <>
      <div className="bar">
        <div>
          <h3>{t('abonnement.titre')}</h3>
        </div>
      </div>
      <div className="body">
      {/* Les trois compteurs de la formule (`QUOTAS`), quelle que soit la
          formule, avant tout ce qui concerne le paiement. */}
      <Compteurs />
      <section className="card">
        {e.abonne ? (
          <>
            <p>
              {e.plan && <b>{nomPlan(e.plan)}. </b>}
              {!e.jusquA
                ? t('abonnement.abonne')
                : e.statut === 'resilie'
                  ? t('abonnement.finLe', { date: fullDate(e.jusquA) })
                  : t('abonnement.abonneJusqua', { date: fullDate(e.jusquA) })}
            </p>
            {e.statut === 'en_retard' && <p className="avertissement">{t('abonnement.enRetard')}</p>}
            {e.gerable && <Portail reprendre={e.statut === 'resilie'} />}
          </>
        ) : retour === 'ok' && attente ? (
          <p role="status">{t('abonnement.confirmation')}</p>
        ) : (
          <>
            {retour === 'ok' && <p role="status">{t('abonnement.confirmationLongue')}</p>}
            {retour === 'annulee' && <p role="status">{t('abonnement.annule')}</p>}
            {situation && <p>{situation}</p>}
            {e.ouvert && e.catalogue ? (
              <>
                {anneeProposee && (
                  <div className="segstatus" role="group" aria-label={t('abonnement.duree')}>
                    <button className={`seg${intervalle === 'mois' ? ' on' : ''}`} aria-pressed={intervalle === 'mois'} onClick={() => setIntervalle('mois')}>
                      {t('abonnement.auMois')}
                    </button>
                    <button className={`seg${intervalle === 'an' ? ' on' : ''}`} aria-pressed={intervalle === 'an'} onClick={() => setIntervalle('an')}>
                      {t('abonnement.alAnnee')}
                    </button>
                  </div>
                )}
                <div className="plans" id="plans">
                  {(['radar', 'complet'] as Plan[]).map((plan) => {
                    const p = e.catalogue?.[plan]?.[intervalle]
                    if (!p) return null
                    return (
                      <div key={plan} className="plan">
                        <h4>{nomPlan(plan)}</h4>
                        <p>
                          <b>{prixLisible(p, lang)}</b> {parIntervalle(p)}, {t('abonnement.ttc')}
                        </p>
                        <ul>
                          {(plan === 'complet'
                            ? [t('abonnement.complet.l1'), t('abonnement.complet.l2'), t('abonnement.complet.l3'), t('abonnement.complet.l4')]
                            : [t('abonnement.radar.l1'), t('abonnement.radar.l2'), t('abonnement.radar.l3'), t('abonnement.radar.l4')]
                          ).map((l) => (
                            <li key={l}>{l}</li>
                          ))}
                        </ul>
                        <ActionButton
                          variant={plan === 'radar' ? 'primary' : 'secondary'}
                          busyLabel={t('abonnement.redirection')}
                          onClick={async () => {
                            const { url } = await post<{ url: string }>('/api/abonnement/caisse', { plan, intervalle })
                            window.location.href = url
                          }}
                        >
                          <CreditCard size={15} /> {t('abonnement.choisir', { plan: nomPlan(plan) })}
                        </ActionButton>
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <p className="field-help">{t('abonnement.pasOuvert')}</p>
            )}
            {/* Un ancien abonné retrouve ses factures au portail. */}
            {e.gerable && <Portail reprendre={false} />}
          </>
        )}
      </section>
      </div>
    </>
  )
}

/** Un compteur : `limite` nulle, sans limite ; 0, réservé à Complet. */
interface Compteur {
  utilisees: number
  limite: number | null
  remise?: string
}

interface CompteursRadar {
  adaptations: Compteur
  employeurs: { suivis: number; limite: number | null }
  recherches: Compteur
}

/** Ce que la formule permet et ce qui en est pris : CV adaptés de la semaine,
 *  employeurs suivis, recherches du mois (`GET /api/radar/compteurs`). */
function Compteurs() {
  const t = useT()
  const c = useData(() => get<CompteursRadar>('/api/radar/compteurs'), [])
  if (!c.data) return null
  const { adaptations, employeurs, recherches } = c.data
  return (
    <section className="card compteurs" aria-labelledby="compteurs-titre">
      <h4 id="compteurs-titre">{t('compteurs.titre')}</h4>
      <div className="compteurs-grille">
        <Tuile label={t('compteurs.cv')} c={adaptations} />
        <Tuile label={t('compteurs.employeurs')} c={{ utilisees: employeurs.suivis, limite: employeurs.limite }} />
        <Tuile label={t('compteurs.recherches')} c={recherches} />
      </div>
    </section>
  )
}

function Tuile({ label, c }: { label: string; c: Compteur }) {
  const t = useT()
  const reserve = c.limite === 0
  const plein = c.limite !== null && c.limite > 0 && c.utilisees >= c.limite
  const part = c.limite ? Math.min(1, c.utilisees / c.limite) : 0
  const pied = reserve
    ? t('compteurs.avecComplet')
    : c.limite === null
      ? t('compteurs.sansLimite')
      : plein
        ? c.remise
          ? t('compteurs.atteint', { date: fullDate(c.remise) })
          : t('compteurs.plein')
        : c.remise
          ? t('compteurs.repart', { date: fullDate(c.remise) })
          : null
  return (
    <div className={`tuile-compteur${plein ? ' plein' : ''}${reserve ? ' reserve' : ''}`}>
      <span className="tc-label">{label}</span>
      <span className="tc-valeur">
        <b>{c.utilisees}</b>
        {c.limite !== null && c.limite > 0 && <span> {t('compteurs.sur', { max: c.limite })}</span>}
      </span>
      {c.limite !== null && c.limite > 0 && (
        <span className="tc-barre" role="progressbar" aria-valuemin={0} aria-valuemax={c.limite} aria-valuenow={c.utilisees} aria-label={label}>
          <span style={{ width: `${part * 100}%` }} />
        </span>
      )}
      {pied && <span className="tc-pied">{reserve ? <a href="#plans">{pied}</a> : pied}</span>}
    </div>
  )
}

/** Le portail client de Stripe (issue #113) : carte, factures, résiliation en
 *  fin de période et reprise d'un abonnement résilié. Ce qui y change revient
 *  par le webhook ; la page le relit au retour. */
function Portail({ reprendre }: { reprendre: boolean }) {
  const t = useT()
  return (
    <ActionButton
      variant={reprendre ? 'primary' : 'secondary'}
      busyLabel={t('abonnement.redirection')}
      onClick={async () => {
        const { url } = await post<{ url: string }>('/api/abonnement/portail', {})
        window.location.href = url
      }}
    >
      {reprendre ? t('abonnement.reprendre') : t('abonnement.gerer')}
    </ActionButton>
  )
}
